import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useProductDetail from '../hooks/useProductDetail';
import useRecrawl from '../hooks/useRecrawl';
import useSnackbar from '../hooks/useSnackbar';
import { useProductUpdate } from '../hooks/useProductUpdate';
import { useShopify } from '../hooks/useShopify';
import Button from '../components/Button';
import Snackbar from '../components/Snackbar';
import ThumbnailSlider from '../components/product/detail/ThumbnailSlider';
import ProductBasicInfo from '../components/product/detail/ProductBasicInfo';
import ProductTitle from '../components/product/detail/ProductTitle';
import ProductVendorDatePicker from '../components/product/detail/ProductVendorDatePicker';
import ProductTags from '../components/product/detail/ProductTags';
import ProductActionButtons from '../components/product/detail/ProductActionButtons';
import ProductDetailHeader from '../components/product/detail/ProductDetailHeader';
import ProductEditor from '../components/ProductEditor';
import ScrollToTopButton from '../components/ScrollToTopButton';

const ProductDetail = () => {
const { id } = useParams();
const navigate = useNavigate();
const { product, loading, error, refetch, checkAndUpdateShopifyStatus } = useProductDetail(id);
const { recrawling, recrawlProduct } = useRecrawl();
const { snackbar, showSnackbar, closeSnackbar } = useSnackbar();
const { saving, updateProductData, updateProductTitle, updateProductPriceUsd, updateProductDetail } = useProductUpdate(showSnackbar);
const { publishProduct, updateShopifyProduct, updateProductStatus } = useShopify(showSnackbar, updateProductData);


// 상세 정보 편집 상태
const [editingDetail, setEditingDetail] = useState(false);
const [detailContent, setDetailContent] = useState('');
const [selectedContentImages, setSelectedContentImages] = useState(new Set());

// 썸네일 관련 상태
const [selectedImage, setSelectedImage] = useState(null);
const [selectedImageIndex, setSelectedImageIndex] = useState(0);
const [selectedThumbnails, setSelectedThumbnails] = useState(new Set());
const [thumbnailImages, setThumbnailImages] = useState([]);

// 제품명 편집 상태 (영문만)
const [editingTitleEn, setEditingTitleEn] = useState(false);
const [tempTitleEn, setTempTitleEn] = useState('');

// USD 가격 편집 상태
const [editingPriceUsd, setEditingPriceUsd] = useState(false);
const [tempPriceUsd, setTempPriceUsd] = useState('');

// description 확장 상태
const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);

// 게시 상태
const [publishing, setPublishing] = useState(false);

// 상태 변경 상태
const [updatingStatus, setUpdatingStatus] = useState(false);

// 벤더 및 태그 상태
const [selectedVendor, setSelectedVendor] = useState('');
const [tags, setTags] = useState([]);
const [newTag, setNewTag] = useState('');

// 제품 로드 시 Shopify 상태 확인
useEffect(() => {
    if (!product || !product.id) return;
    
    let isMounted = true; // 컴포넌트가 마운트되어 있는지 확인하는 플래그
    
    // Shopify 상태 확인 및 DB 업데이트 (비동기로 실행, 블로킹하지 않음)
    checkAndUpdateShopifyStatus(product, updateProductData).then((updatedProduct) => {
        // 상태가 업데이트되었을 때만 제품 정보 다시 불러오기
        if (updatedProduct && isMounted) {
            console.log('상태가 업데이트되어 제품 정보를 다시 불러옵니다.');
            // 약간의 지연을 두어 DB 업데이트가 완료되도록 함
            setTimeout(() => {
                if (isMounted) {
                    refetch();
                }
            }, 500);
        } else if (isMounted) {
            console.log('상태가 변경되지 않았으므로 refetch하지 않습니다.');
        }
    }).catch(err => {
        // 에러는 checkAndUpdateShopifyStatus 내부에서 처리되므로 여기서는 무시
        if (isMounted) {
            console.warn('Shopify 상태 확인 실패:', err);
        }
    });
    
    return () => {
        isMounted = false; // cleanup 함수
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
}, [product?.id, product?.shopify_product_id]); // shopify_product_id가 변경될 때도 재확인

// 제품 데이터 로드 시 썸네일 이미지 초기화
useEffect(() => {
    if (!product) return;

    // 썸네일 이미지 배열 구성 (media_images 사용)
    const allImages = product.media_images || [];
    
    setThumbnailImages(allImages);
    
    // 모든 썸네일을 기본 선택 상태로 설정
    const allSelected = new Set(allImages.map((_, index) => index));
    setSelectedThumbnails(allSelected);
    
    // 메인 이미지 설정 (media_images[0] 사용)
    if (allImages.length > 0) {
        setSelectedImage(allImages[0]);
        setSelectedImageIndex(0);
    } else {
        setSelectedImage(null);
        setSelectedImageIndex(0);
    }

    // 상세 정보 초기화
    if (product && product.description) {
        setDetailContent(product.description);
    } else if (product && product.content_images && product.content_images.length > 0) {
        // description이 없으면 content_images를 HTML로 변환하여 초기화
        const imageHTML = product.content_images
            .map(imageUrl => `<img src="${imageUrl}" alt="상세 이미지" style="max-width: 400px; height: auto; margin: 10px 0;" />`)
            .join('\n');
        setDetailContent(imageHTML);
    }

    // content_images 선택 상태 초기화 (모든 이미지를 기본 선택)
    if (product && product.content_images && product.content_images.length > 0) {
        const allSelected = new Set(product.content_images.map((_, index) => index));
        setSelectedContentImages(allSelected);
    }

    // 벤더 및 태그 초기화
    if (product) {
        // 벤더 초기화: product.vendor가 존재하고 유효한 값이면 설정
        if (product.vendor && product.vendor.trim() !== '') {
            setSelectedVendor(product.vendor);
        } else {
            setSelectedVendor('');
        }
        
        // 태그 초기화 (기존 태그만 포함)
        const initialTags = [];
        if (product.tags && Array.isArray(product.tags)) {
            initialTags.push(...product.tags);
        }
        setTags(initialTags);
        
        // 도착일 및 출하일 초기화
        if (product.arrival_date) {
            setArrivalDate({
                start: product.arrival_date.start || '',
                end: product.arrival_date.end || ''
            });
        }
        
        if (product.shipment_date) {
            setShipmentDate({
                start: product.shipment_date.start || '',
                end: product.shipment_date.end || ''
            });
        }
    }
}, [product]);

const handleBack = () => {
    navigate('/products');
};


// 상세 정보 편집 핸들러들
const handleDetailEdit = () => {
    setEditingDetail(true);
};

// 상세 이미지 선택/해제 핸들러
const handleContentImageToggle = (index) => {
    const newSelected = new Set(selectedContentImages);
    if (newSelected.has(index)) {
        newSelected.delete(index);
    } else {
        newSelected.add(index);
    }
    setSelectedContentImages(newSelected);
};

const handleDetailSave = async (htmlContent, filteredContentImages) => {
    try {
        await updateProductDetail(product, htmlContent, filteredContentImages);
        setDetailContent(htmlContent);
        setEditingDetail(false);
        // 저장 후 데이터 새로고침
        refetch();
    } catch (error) {
        console.error('상세 정보 저장 실패:', error);
    }
};

const handleDetailCancel = () => {
    setEditingDetail(false);
};


// 벤더 및 태그 관련 핸들러들

const handleVendorDateSave = async () => {
    try {
        // 현재 스크롤 위치 저장
        const currentScrollY = window.scrollY;
        
        const updates = {
            vendor: selectedVendor,
            arrival_date: arrivalDate,
            shipment_date: shipmentDate
        };
        
        await updateProductData(product, updates, '벤더 및 날짜 정보가 저장되었습니다.');
        await refetch();
        
        // 리패치 후 스크롤 위치 복원
        setTimeout(() => {
            window.scrollTo(0, currentScrollY);
        }, 100);
    } catch (error) {
        console.error('벤더/날짜 저장 실패:', error);
    }
};

const handleTagsSave = async () => {
    try {
        // 현재 스크롤 위치 저장
        const currentScrollY = window.scrollY;
        
        const updates = {
            tags: tags
        };
        
        await updateProductData(product, updates, '태그가 저장되었습니다.');
        await refetch();
        
        // 리패치 후 스크롤 위치 복원
        setTimeout(() => {
            window.scrollTo(0, currentScrollY);
        }, 100);
    } catch (error) {
        console.error('태그 저장 실패:', error);
    }
};

const handleAddTag = (tagToAdd = null) => {
    const tag = tagToAdd || (newTag ? newTag.trim() : '');
    
    // 빈 문자열이거나 공백만 있는 경우 처리하지 않음
    if (!tag || tag.length === 0) {
        return;
    }
    
    // 중복 태그 체크
    if (!tags.includes(tag)) {
        setTags([...tags, tag]);
        if (!tagToAdd) {
            setNewTag('');
        }
    }
};

const handleRemoveTag = (tagToRemove) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
};

const handleTagKeyPress = (e) => {
    if (e.key === 'Enter') {
        e.preventDefault();
        handleAddTag();
    }
};

// 썸네일 선택 핸들러
const handleThumbnailSelect = (index) => {
    setSelectedImage(thumbnailImages[index]);
    setSelectedImageIndex(index);
};

// 썸네일 체크박스 핸들러
const handleThumbnailCheckbox = (index) => {
    const newSelected = new Set(selectedThumbnails);
    if (newSelected.has(index)) {
        newSelected.delete(index);
    } else {
        newSelected.add(index);
    }
    
    setSelectedThumbnails(newSelected);
};

// 썸네일 순서 변경 핸들러
const handleThumbnailReorder = (fromIndex, toIndex) => {
    const newThumbnailImages = [...thumbnailImages];
    const [movedImage] = newThumbnailImages.splice(fromIndex, 1);
    newThumbnailImages.splice(toIndex, 0, movedImage);
    
    setThumbnailImages(newThumbnailImages);
    
    // 선택된 썸네일 인덱스들도 업데이트
    const newSelectedThumbnails = new Set();
    selectedThumbnails.forEach(selectedIndex => {
        if (selectedIndex === fromIndex) {
            newSelectedThumbnails.add(toIndex);
        } else if (fromIndex < toIndex) {
            // 오른쪽으로 이동한 경우
            if (selectedIndex > fromIndex && selectedIndex <= toIndex) {
                newSelectedThumbnails.add(selectedIndex - 1);
            } else {
                newSelectedThumbnails.add(selectedIndex);
            }
        } else {
            // 왼쪽으로 이동한 경우
            if (selectedIndex >= toIndex && selectedIndex < fromIndex) {
                newSelectedThumbnails.add(selectedIndex + 1);
            } else {
                newSelectedThumbnails.add(selectedIndex);
            }
        }
    });
    setSelectedThumbnails(newSelectedThumbnails);
    
    // 선택된 이미지 인덱스도 업데이트
    if (selectedImageIndex === fromIndex) {
        setSelectedImageIndex(toIndex);
    } else if (fromIndex < toIndex) {
        if (selectedImageIndex > fromIndex && selectedImageIndex <= toIndex) {
            setSelectedImageIndex(selectedImageIndex - 1);
        }
    } else {
        if (selectedImageIndex >= toIndex && selectedImageIndex < fromIndex) {
            setSelectedImageIndex(selectedImageIndex + 1);
        }
    }
};

// 액션 버튼 핸들러들
const handleDelete = () => {
    if (window.confirm('정말로 이 제품을 삭제하시겠습니까?')) {
        showSnackbar('제품 삭제 기능이 구현되었습니다.', 'info');
    }
};

const handleSave = async () => {
    if (!product) return;
    
    // 현재 스크롤 위치 저장
    const currentScrollY = window.scrollY;
    
    // 선택된 썸네일들만 필터링
    const selectedImages = thumbnailImages.filter((_, index) => selectedThumbnails.has(index));
    
    // thumbnailImages가 비어있으면 기존 media_images 사용
    const finalMediaImages = thumbnailImages.length > 0 ? selectedImages : (product.media_images || []);
    
    // 모든 업데이트 데이터를 하나의 요청으로 통합
    const updates = {
        media_images: finalMediaImages,
        vendor: selectedVendor,
        tags: tags,
        arrival_date: arrivalDate,
        shipment_date: shipmentDate
    };
    
    // updateProductData를 사용하여 모든 정보를 한 번에 저장
    const result = await updateProductData(product, updates, '제품 정보가 저장되었습니다.');
    
    if (result.success) {
        await refetch();
        
        // 리패치 후 스크롤 위치 복원
        setTimeout(() => {
            window.scrollTo(0, currentScrollY);
        }, 10);
    }
};

const handlePublish = async () => {
    setPublishing(true);
    try {
        await publishProduct(product);
        // 게시 성공 후 데이터 새로고침
        refetch();
    } catch (error) {
        console.error('제품 게시 실패:', error);
    } finally {
        setPublishing(false);
    }
};

const handleRecrawl = async () => {
    try {
        await recrawlProduct(product);
        // 재크롤링 성공 후 제품 정보 새로고침
        await refetch();
        showSnackbar('재크롤링이 완료되었습니다.', 'success');
    } catch (error) {
        showSnackbar('재크롤링 중 오류가 발생했습니다.', 'error');
    }
};

const handleUpdateShopify = async () => {
    try {
        await updateShopifyProduct(product);
        // 업데이트 성공 후 데이터 새로고침
        await refetch();
    } catch (error) {
        console.error('Shopify 제품 업데이트 실패:', error);
    }
};

const handleStatusChange = async (newStatus) => {
    setUpdatingStatus(true);
    try {
        await updateProductStatus(product, newStatus);
        // 상태 변경 성공 후 데이터 새로고침
        await refetch();
    } catch (error) {
        console.error('제품 상태 변경 실패:', error);
    } finally {
        setUpdatingStatus(false);
    }
};

// 제품명 편집 핸들러들 (영문만)
const handleTitleEnClick = () => {
    setTempTitleEn(product.title_en || '');
    setEditingTitleEn(true);
};

const handleTitleEnSave = async () => {
    if (!product) return;
    
    // 현재 스크롤 위치 저장
    const currentScrollY = window.scrollY;
    
    const result = await updateProductTitle(product, tempTitleEn);
    
    if (result.success) {
        setEditingTitleEn(false);
        await refetch();
        
        // 리패치 후 스크롤 위치 복원
        setTimeout(() => {
            window.scrollTo(0, currentScrollY);
        }, 100);
    }
};

const handleTitleEnCancel = () => {
    setEditingTitleEn(false);
    setTempTitleEn('');
};

// USD 가격 편집 핸들러들
const handlePriceUsdClick = () => {
    setTempPriceUsd(product.price_usd?.toString() || '');
    setEditingPriceUsd(true);
};

const handlePriceUsdSave = async () => {
    if (!product) return;
    
    const priceUsdValue = parseFloat(tempPriceUsd);
    if (isNaN(priceUsdValue) || priceUsdValue < 0) {
        showSnackbar('올바른 USD 가격을 입력해주세요.', 'error');
        return;
    }

    // 현재 스크롤 위치 저장
    const currentScrollY = window.scrollY;

    const result = await updateProductPriceUsd(product, priceUsdValue);
    
    if (result.success) {
        setEditingPriceUsd(false);
        await refetch();
        
        // 리패치 후 스크롤 위치 복원
        setTimeout(() => {
            window.scrollTo(0, currentScrollY);
        }, 100);
    }
};

const handlePriceUsdCancel = () => {
    setEditingPriceUsd(false);
    setTempPriceUsd('');
};

// 도착일 및 출하일 상태 관리
const [arrivalDate, setArrivalDate] = useState({ start: '', end: '' });
const [shipmentDate, setShipmentDate] = useState({ start: '', end: '' });

// 도착일 및 출하일 핸들러들 (즉시 저장하지 않음)
const handleArrivalDateChange = (newArrivalDate) => {
    setArrivalDate(newArrivalDate);
};

const handleShipmentDateChange = (newShipmentDate) => {
    setShipmentDate(newShipmentDate);
};

if (loading) {
    return (
    <div className="bg-white rounded-lg p-8 shadow-md max-w-7xl mx-auto">
        <div className="text-center py-10 text-gray-600">
        제품 상세 정보를 불러오는 중...
        </div>
    </div>
    );
}

if (error) {
    return (
    <div className="bg-white rounded-lg p-8 shadow-md max-w-7xl mx-auto">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-5">
        {error}
        </div>
        <Button
        onClick={handleBack}
        variant="primary"
        size="md"
        >
        목록으로 돌아가기
        </Button>
    </div>
    );
}

if (!product) {
    return (
    <div className="bg-white rounded-lg p-8 shadow-md max-w-7xl mx-auto">
        <div className="text-center py-10 text-gray-600">
        제품 정보를 찾을 수 없습니다.
        </div>
        <Button
        onClick={handleBack}
        variant="primary"
        size="md"
        >
        목록으로 돌아가기
        </Button>
    </div>
    );
}

// 상태 설정 (미게시 / 초안 / 활성화)
const getProductStatusConfig = (product) => {
    if (!product.shopify_product_id) {
        return { text: '미게시', className: 'bg-gray-100 text-gray-800' };
    }
    
    if (product.status === 'draft') {
        return { text: '초안', className: 'bg-yellow-100 text-yellow-800' };
    }
    
    if (product.status === 'active') {
        return { text: '활성화', className: 'bg-green-100 text-green-800' };
    }
    
    return { text: '미게시', className: 'bg-gray-100 text-gray-800' };
};

const statusConfig = getProductStatusConfig(product);
const showUpdateButton = product.shopify_product_id && (product.status === 'draft' || product.status === 'active');

return (
    <>
    <div className="bg-white rounded-lg p-8 shadow-lg max-w-7xl mx-auto">
    {/* 헤더 */}
    <ProductDetailHeader
        recrawling={recrawling}
        statusConfig={statusConfig}
        onBack={handleBack}
        onRecrawl={handleRecrawl}
        onUpdate={handleUpdateShopify}
        showUpdateButton={showUpdateButton}
    />

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 이미지 섹션 */}
        <div className="space-y-4">
            {/* 메인 이미지 */}
            <div className="bg-gray-100 rounded-lg p-4">
                {selectedImage ? (
                <img
                    src={selectedImage}
                    alt={product.title || product.title_en}
                    className="w-full h-80 object-contain rounded"
                    onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                    }}
                />
                ) : (
                <div className="w-full h-80 bg-gray-200 rounded flex items-center justify-center text-gray-500">
                    이미지 없음
                </div>
                )}
            </div>

            {/* 썸네일 슬라이드 */}
            <ThumbnailSlider
                thumbnailImages={thumbnailImages}
                selectedRepImage={selectedImage}
                selectedThumbnails={selectedThumbnails}
                onThumbnailSelect={handleThumbnailSelect}
                onThumbnailCheckbox={handleThumbnailCheckbox}
                onThumbnailReorder={handleThumbnailReorder}
            />

            {/* 브랜드 및 카테고리 정보 */}
            {/* <ProductBrandCategory
                product={product}
                onVendorTagsSave={handleVendorTagsSave}
            /> */}

            {/* 제품명 */}
            <ProductTitle
                product={product}
                editingTitleEn={editingTitleEn}
                tempTitleEn={tempTitleEn}
                onTitleEnClick={handleTitleEnClick}
                onTitleEnSave={handleTitleEnSave}
                onTitleEnCancel={handleTitleEnCancel}
                onTempTitleEnChange={setTempTitleEn}
            />
            
            {/* 태그 편집 영역 */}
            <ProductTags
                tags={tags}
                newTag={newTag}
                onTagAdd={handleAddTag}
                onTagRemove={handleRemoveTag}
                onTagKeyPress={handleTagKeyPress}
                onNewTagChange={setNewTag}
                onSave={handleTagsSave}
            />
        </div>

        {/* 제품 정보 섹션 */}
        <div className="space-y-6">
            {/* 기본 정보 */}
            <ProductBasicInfo
                product={product}
                editingPriceUsd={editingPriceUsd}
                tempPriceUsd={tempPriceUsd}
                onPriceUsdClick={handlePriceUsdClick}
                onPriceUsdSave={handlePriceUsdSave}
                onPriceUsdCancel={handlePriceUsdCancel}
                onTempPriceUsdChange={setTempPriceUsd}
                onStatusChange={handleStatusChange}
                updatingStatus={updatingStatus}
            />

            {/* 벤더 및 날짜 피커 */}
            <ProductVendorDatePicker
                selectedVendor={selectedVendor}
                arrivalDate={arrivalDate}
                shipmentDate={shipmentDate}
                onVendorChange={setSelectedVendor}
                onArrivalDateChange={handleArrivalDateChange}
                onShipmentDateChange={handleShipmentDateChange}
                onSave={handleVendorDateSave}
            />



        </div>
    </div>

    {/* 제품 상세 정보 */}
    <div className="bg-white rounded-lg p-6 shadow-lg max-w-7xl mx-auto mt-6">
        <div className="flex mb-4 justify-between">
            <h3 className="text-lg font-semibold text-gray-800 ">상세 페이지</h3>
            {!editingDetail && (
                <Button
                    onClick={handleDetailEdit}
                    variant="basicNavy"
                    size="sm"
                >
                    편집
                </Button>
            )}
        </div>

        {editingDetail ? (
            <ProductEditor
                initialContent={detailContent}
                contentImages={product?.content_images || []}
                selectedContentImages={selectedContentImages}
                onContentImageToggle={handleContentImageToggle}
                onSave={handleDetailSave}
                onCancel={handleDetailCancel}
                height="600px"
            />
        ) : (
            <div className="space-y-4 ">
                {/* 제품 URL */}
                {product.url && (
                    <div className="bg-gray-50 rounded-lg p-4 ">
                        <span className="text-gray-600 font-medium">제품 URL:</span>
                        <a
                            href={product.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 mt-1 block break-all"
                        >
                            {product.url}
                        </a>
                    </div>
                )}
                
                {/* 상세 정보 프리뷰 */}
                <div className="prose max-w-md">
                    {detailContent ? (
                        <div>
                            <div className={`text-gray-700 flex flex-col items-center ${!isDescriptionExpanded ? 'max-h-[600px] overflow-hidden' : ''}`}>
                                <div 
                                    className="prose-content"
                                    dangerouslySetInnerHTML={{ __html: detailContent }} 
                                />
                            </div>
                            <button
                                onClick={() => setIsDescriptionExpanded(!isDescriptionExpanded)}
                                className="mt-2 text-blue-600 hover:text-blue-800 text-m font-medium"
                            >
                                {isDescriptionExpanded ? '접기' : '더보기'}
                            </button>
                        </div>
                    ) : (
                        <div className="text-gray-500 italic">
                            상세 정보가 없습니다. 편집 버튼을 클릭하여 추가하세요.
                        </div>
                    )}
                </div>
            </div>
        )}
    </div>
    </div>

    {/* 액션 버튼들 */}
    <ProductActionButtons
        product={product}
        saving={saving}
        publishing={publishing}
        onDelete={handleDelete}
        onSave={handleSave}
        onPublish={handlePublish}
    />
    
    {/* 스낵바 */}
    <Snackbar
        isOpen={snackbar.isOpen}
        message={snackbar.message}
        type={snackbar.type}
        onClose={closeSnackbar}
    />
    
    {/* 스크롤 최상단 버튼 */}
    <ScrollToTopButton />
    </>
);
};

export default ProductDetail;
